import { paths, type Client } from '@acs/shared';
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import {
  appointmentConverter,
  clientConverter,
  type AppointmentDoc,
  type ClientDoc,
} from './converters.js';
import { db } from './firebase.js';

/** Thrown by {@link createClient} when a record already exists for the number,
 * so the dialog can offer "Open profile" instead of surfacing a rules error
 * (doc 12 §6). */
export class DuplicateClientError extends Error {
  constructor(readonly phone: string) {
    super(`A client with ${phone} already exists`);
    this.name = 'DuplicateClientError';
  }
}

/** Live client base. Ordering is done client-side (doc 12 §4, `sortClients`),
 * NOT via a Firestore `orderBy`: a field-based order-by silently EXCLUDES docs
 * that lack that field, which would hide every manually-added client (they have
 * no `lastSeenAt` until their first booking). Client bases are hundreds of docs
 * (doc 11 §7), so fetching the whole collection unordered is fine. */
export function subscribeClients(businessId: string, cb: (rows: ClientDoc[]) => void): () => void {
  const q = query(collection(db, paths.clients(businessId)).withConverter(clientConverter));
  return onSnapshot(q, (snap) => cb(snap.docs.map((d) => d.data())));
}

/** Single client by E.164 doc id (detail page). */
export function subscribeClient(
  businessId: string,
  e164: string,
  cb: (client: ClientDoc | null) => void,
): () => void {
  const ref = doc(db, paths.client(businessId, e164)).withConverter(clientConverter);
  return onSnapshot(ref, (snap) => cb(snap.exists() ? snap.data() : null));
}

/** One client's booking history, most recent first (doc 12 §7 detail; index doc 11 §8). */
export function subscribeClientAppointments(
  businessId: string,
  e164: string,
  cb: (rows: AppointmentDoc[]) => void,
): () => void {
  const q = query(
    collection(db, paths.appointments(businessId)).withConverter(appointmentConverter),
    where('customerPhone', '==', e164),
    orderBy('start', 'desc'),
  );
  return onSnapshot(q, (snap) => cb(snap.docs.map((d) => d.data())));
}

export interface NewClientInput {
  phone: string;
  name: string;
  notes?: string;
}

/** Manually add a client (doc 12 §6). Writes profile fields only — stats stay
 * server-owned (rules reject them). Point-reads first so a duplicate becomes a
 * typed error the dialog turns into an "Open profile" link, not a rules failure. */
export async function createClient(businessId: string, input: NewClientInput): Promise<void> {
  const ref = doc(db, paths.client(businessId, input.phone)).withConverter(clientConverter);
  const existing = await getDoc(ref);
  if (existing.exists()) throw new DuplicateClientError(input.phone);
  // Written as a plain doc (not through the converter, which would demand the
  // full model incl. server-owned stats): profile fields only, matching the
  // rules' clientCreatableKeys allowlist.
  await setDoc(doc(db, paths.client(businessId, input.phone)), {
    phone: input.phone,
    name: input.name,
    notes: input.notes ?? '',
    source: 'manual' satisfies Client['source'],
    createdAt: serverTimestamp(),
  });
}

/** Owner edit — name/notes only (the sole mutable fields, doc 12 §7 / rules). */
export async function updateClient(
  businessId: string,
  e164: string,
  patch: { name?: string; notes?: string },
): Promise<void> {
  await updateDoc(doc(db, paths.client(businessId, e164)), {
    ...patch,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Revoke messaging consent on a client's behalf (doc 21 §5).
 *
 * Revoke ONLY — there is no matching "grant". Consent belongs to the customer,
 * so an owner raising it from the portal would be manufacturing it; the rules
 * enforce that, this function simply has no way to express it. The counterpart
 * is a customer saying "stop texting me" at the counter, which must be
 * honourable immediately rather than waiting for an engineer.
 *
 * Permanent by design: it suppresses reminders AND the transactional messages,
 * and only an explicit opt-in in a later conversation can clear it.
 */
export async function revokeClientConsent(businessId: string, e164: string): Promise<void> {
  await updateDoc(doc(db, paths.client(businessId, e164)), {
    reminders: { status: 'revoked', prompt: '', revokedAt: serverTimestamp() },
    updatedAt: serverTimestamp(),
  });
}

/** GDPR erasure of the profile. Appointments/calls are kept (doc 11 §9). */
export async function deleteClient(businessId: string, e164: string): Promise<void> {
  await deleteDoc(doc(db, paths.client(businessId, e164)));
}
